'use client';

import shapeRetangle from '@/public/service/shapeRetangle.svg';
import Image from 'next/image';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { CTAButton } from '@/shadcn/ui/button';

interface IProcessTreeProps {
  items: {
    text: string;
    hasTools?: boolean;
    tools?: GROQImage[];
    hasChildrens?: boolean;
    childrens?: {
      text: string;
    }[];
    description?: string;
  }[];
  itemsTwo: {
    text: string;
    hasTools?: boolean;
    tools?: GROQImage[];
    hasChildrens?: boolean;
    childrens?: {
      text: string;
    }[];
    description?: string;
  }[];
  itemsThree: {
    text: string;
    hasTools?: boolean;
    tools?: GROQImage[];
    hasChildrens?: boolean;
    childrens?: {
      text: string;
      hasTools?: boolean;
      tools?: GROQImage[];
    }[];
    description?: string;
  }[];
  itemsFour: {
    text: string;
    hasTools?: boolean;
    tools?: GROQImage[];
  }[];
  ctaButton: GROQButton;
}

export default function ProcessTree({
  items,
  itemsTwo,
  itemsThree,
  itemsFour,
  ctaButton,
}: IProcessTreeProps) {
  return (
    <section className="w-full flex flex-col items-center py-[13rem] lg:pt-[9.63rem] lg:pb-[16.44rem] relative">
      <div className="container flex flex-col gap-[5rem] lg:gap-[4rem] items-center justify-center w-full relative z-10">
        {/* Items One */}
        {items?.length > 0 && items?.map((item, index) => (
          <div key={index} className={`flex flex-col items-center justify-center gap-[2rem] lg:gap-[1rem] w-full relative ${index === 4 ? 'mb-[5.72rem] lg:mb-0' : 'mb-0'}`}>
            <motion.div
              className="flex items-center justify-center p-[1.25rem]"
              initial={{ opacity: 0.08 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.25, ease: easeOut }}
            >
              <p className="text-[2rem] font-[400] text-fakeblack capitalize leading-[140%] font-inter-tight mb-0">{item.text}</p>
              <Image src={shapeRetangle} className="absolute w-[27.89rem] h-[5.5rem] z-0" alt="Shape Retangle" width={226} height={46} />
            </motion.div>

            {item.hasTools && item.tools && item.tools?.length > 0 && (
              <div className="flex flex-wrap lg:flex-row items-center justify-center gap-x-[5rem] gap-y-[3rem] w-full">
                {item.tools.map((tool, toolIndex) => {
                  const totalTools = item.tools!.length;
                  const isOdd = totalTools % 2 !== 0;
                  const middleIndex = Math.floor(totalTools / 2);
                  const isMiddle = isOdd && toolIndex === middleIndex;
                  const fromLeft = toolIndex < middleIndex;

                  return (
                    <motion.div
                      key={toolIndex}
                      initial={{ opacity: 0, x: isMiddle ? 0 : (fromLeft ? -50 : 50) }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, ease: easeOut, delay: 0.25 }}
                      className="w-auto h-[5rem]"
                    >
                      <Image
                        src={tool.src}
                        alt="Partner Logo"
                        className="w-auto h-[5rem] object-contain"
                        width={100}
                        height={80}
                      />
                    </motion.div>
                  );
                })}
              </div>
            )}
            {!item.hasChildrens ? (
              <div className="overflow-clip w-[3rem] flex items-center">
                <motion.div
                  initial={{ y: '-5rem', opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.7, ease: easeOut }}
                >
                  <svg
                    className="w-[3rem] h-auto"
                    viewBox="0 0 24 69"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="11" width="2" height="50" fill="#011410"/>
                    <path d="M18.4425 59.4425L13 64.5V50H11V64.5L5.5575 59.4425L4 61L12 68.5L20 61L18.4425 59.4425Z" fill="#011410"/>
                  </svg>
                </motion.div>
              </div>
            ) : (
              <div className="overflow-clip w-[3rem] flex items-center">
                <motion.div
                  initial={{ y: '-5rem', opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.3, ease: easeOut }}
                  className="hidden lg:block"
                >
                  <svg
                    className="w-[3rem] h-auto"
                    viewBox="0 0 24 50"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="11" width="2" height="50" fill="#011410"/>
                  </svg>
                </motion.div>

                <motion.div
                  initial={{ y: '-5rem', opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.7, ease: easeOut }}
                  className="absolute left-[47%] -translate-x-1/2 top-[7rem] lg:hidden"
                >
                  <svg
                    className="w-[3rem] h-auto"
                    viewBox="0 0 24 69"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="11" width="2" height="50" fill="#011410"/>
                    <path d="M18.4425 59.4425L13 64.5V50H11V64.5L5.5575 59.4425L4 61L12 68.5L20 61L18.4425 59.4425Z" fill="#011410"/>
                  </svg>
                </motion.div>
              </div>
            )}
            {item.hasChildrens && item.childrens && item.childrens?.length > 0 && (
              <div className="flex flex-col items-center justify-center w-full lg:-translate-y-[1rem] border-[.35rem] border-fakeblack lg:border-none py-[6.42rem] lg:py-0 lg:min-w-[87.125rem] mt-[2rem] lg:mt-0">
                {item.childrens.length > 1 && (
                  <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.75, delay: 0.25 }}
                    className="bg-fakeblack h-1 hidden lg:block"
                    style={{
                      width: `calc((${item.childrens.length - 1} * 27.92rem) + (${item.childrens.length - 1} * 7rem))`
                    }}
                  />
                )}

                <div className="flex flex-col lg:flex-row items-center justify-center gap-[2.66rem] lg:gap-[7rem] w-full">
                  {item.childrens.map((child, childIndex) => (
                    <div key={childIndex} className="flex flex-col items-center justify-center gap-[2.75rem] w-full lg:w-[27.89706rem]">
                      <div className="overflow-clip w-[3rem] lg:flex items-center hidden">
                        <motion.div
                          initial={{ y: '-5rem', opacity: 0 }}
                          whileInView={{ y: 0, opacity: 1 }}
                          viewport={{ once: true, margin: '-100px' }}
                          transition={{ duration: 0.5, ease: easeOut, delay: 0.5 }}
                        >
                          <svg
                            className="w-[3rem] h-auto"
                            viewBox="0 0 24 43"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect x="11" width="2" height="24" fill="#011410"/>
                            <path d="M18.4425 33.4425L13 38.5V24H11V38.5L5.5575 33.4425L4 35L12 42.5L20 35L18.4425 33.4425Z" fill="#011410"/>
                          </svg>
                        </motion.div>
                      </div>
                      <motion.div
                        initial={{ opacity: 0.08 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.25, ease: easeOut, delay: 0.1 * (childIndex + 1) + 0.75 }}
                        className="flex items-center justify-center p-[1.25rem]"
                      >
                        <p key={childIndex} className="text-[2rem] font-[400] text-fakeblack leading-[140%] font-inter-tight mb-0">{child.text}</p>
                        <Image src={shapeRetangle} className="absolute w-[27.89rem] h-[5.5rem] z-0" alt="Shape Retangle" width={226} height={46} />
                      </motion.div>
                    </div>
                  ))}
                </div>
                <motion.p
                  className="text-[2rem] font-[400] text-fakeblack/[72%] capitalize leading-[140%] font-inter-tight mt-[3rem] lg:mt-[1.87rem] mb-0"
                  initial={{ opacity: 0.08 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.25, ease: easeOut, delay: 1.25 }}
                >
                  {item.description}
                </motion.p>
                <div className="overflow-clip w-[3rem] h-[8.75rem] flex items-center lg:mt-[1.87rem] absolute lg:relative bottom-[-5rem] lg:bottom-auto">
                  <motion.div
                    initial={{ y: '-5rem', opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.7, ease: easeOut }}
                    className=""
                  >
                    <svg
                      className="w-[3rem] h-auto"
                      viewBox="0 0 24 69"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="11" width="2" height="50" fill="#011410"/>
                      <path d="M18.4425 59.4425L13 64.5V50H11V64.5L5.5575 59.4425L4 61L12 68.5L20 61L18.4425 59.4425Z" fill="#011410"/>
                    </svg>
                  </motion.div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Items Two */}
        <div className="flex flex-col gap-[2rem] items-center justify-center w-full relative">
          {itemsTwo?.length > 0 && itemsTwo?.map((item, index) => (
            <div key={index} className="flex flex-col items-center justify-center gap-[2rem] lg:gap-[2.31rem] w-full relative">
              <motion.div
                className="flex items-center justify-center p-[1.25rem]"
                initial={{ opacity: 0.08 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.25, ease: easeOut }}
              >
                <p className="text-[2rem] font-[400] text-fakeblack capitalize leading-[140%] font-inter-tight mb-0">{item.text}</p>
                <Image src={shapeRetangle} className="absolute w-[27.89rem] h-[5.5rem] z-0" alt="Shape Retangle" width={226} height={46} />
              </motion.div>

              {item.hasTools && item.tools && item.tools?.length > 0 && (
                <div className="flex flex-wrap lg:flex-row items-center justify-center gap-x-[5rem] gap-y-[3rem] w-full px-[4rem] lg:px-0">
                  {item.tools.map((tool, toolIndex) => {
                    const totalTools = item.tools!.length;
                    const isOdd = totalTools % 2 !== 0;
                    const middleIndex = Math.floor(totalTools / 2);
                    const isMiddle = isOdd && toolIndex === middleIndex;
                    const fromLeft = toolIndex < middleIndex;

                    return (
                      <motion.div
                        key={toolIndex}
                        initial={{ opacity: 0, x: isMiddle ? 0 : (fromLeft ? -50 : 50) }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, ease: easeOut, delay: 0.25 }}
                        className="w-auto h-[5rem]"
                      >
                        <Image
                          src={tool.src}
                          alt="Partner Logo"
                          className="w-auto h-[5rem] object-contain"
                          width={100}
                          height={80}
                        />
                      </motion.div>
                    );
                  })}
                </div>
              )}
              {!item.hasChildrens ? (
                <div className="overflow-clip w-[3rem] h-[8.75rem] flex lg:mt-[.6rem]">
                  <motion.div
                    initial={{ y: '-5rem' }}
                    whileInView={{ y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.7, ease: easeOut }}
                  >
                    <svg
                      className="w-[3rem] h-auto"
                      viewBox="0 0 24 69"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="11" width="2" height="50" fill="#011410"/>
                      <path d="M18.4425 59.4425L13 64.5V50H11V64.5L5.5575 59.4425L4 61L12 68.5L20 61L18.4425 59.4425Z" fill="#011410"/>
                    </svg>
                  </motion.div>
                </div>
              ) : (
                <div className="overflow-clip w-[3rem] h-[8.75rem] lg:h-[6rem] flex">
                  <motion.div
                    initial={{ y: '-5rem' }}
                    whileInView={{ y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.3, ease: easeOut }}
                    className="hidden lg:block"
                  >
                    <svg
                      className="w-[3rem] h-auto"
                      viewBox="0 0 24 50"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="11" width="2" height="50" fill="#011410"/>
                    </svg>
                  </motion.div>

                  <motion.div
                    initial={{ y: '-5rem' }}
                    whileInView={{ y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.7, ease: easeOut }}
                    className="relative lg:hidden"
                  >
                    <svg
                      className="w-[3rem] h-auto"
                      viewBox="0 0 24 69"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="11" width="2" height="50" fill="#011410"/>
                      <path d="M18.4425 59.4425L13 64.5V50H11V64.5L5.5575 59.4425L4 61L12 68.5L20 61L18.4425 59.4425Z" fill="#011410"/>
                    </svg>
                  </motion.div>
                </div>
              )}
              {item.hasChildrens && item.childrens && item.childrens?.length > 0 && (
                <div className="flex flex-col items-center justify-center gap-[2rem] lg:gap-0 w-full lg:-translate-y-[2.31rem] relative">
                  {item.childrens.length > 1 && (
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true, margin: '-100px' }}
                      transition={{ duration: 0.75 }}
                      className="bg-fakeblack h-1 hidden lg:block w-full max-w-[89.5rem]"
                    />
                  )}

                  <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-between gap-[2.66rem] lg:gap-[7rem] w-full lg:max-w-[117.125rem]">
                    {item.childrens.map((child, childIndex) => (
                      <div key={childIndex} className="flex flex-col items-center justify-center gap-[2.75rem] w-full lg:w-[27.89706rem]">
                        <div className="overflow-clip w-[3rem] lg:flex items-center hidden">
                          <motion.div
                            initial={{ y: '-5rem' }}
                            whileInView={{ y: 0 }}
                            viewport={{ once: true, margin: '-100px' }}
                            transition={{ duration: 0.5, ease: easeOut, delay: 0.25 }}
                          >
                            <svg
                              className="w-[3rem] h-auto"
                              viewBox="0 0 24 43"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect x="11" width="2" height="24" fill="#011410"/>
                              <path d="M18.4425 33.4425L13 38.5V24H11V38.5L5.5575 33.4425L4 35L12 42.5L20 35L18.4425 33.4425Z" fill="#011410"/>
                            </svg>
                          </motion.div>
                        </div>
                        <div className="flex flex-col items-center justify-center">
                          <div className="flex items-center justify-center p-[1.25rem]">
                            <p key={childIndex} className="text-[2rem] font-[400] text-fakeblack leading-[140%] font-inter-tight mb-0">{child.text}</p>
                            <Image src={shapeRetangle} className="absolute w-[27.89rem] h-[5.5rem] z-0" alt="Shape Retangle" width={226} height={46} />
                          </div>
                          <div className="overflow-clip w-[3rem] h-[4.25rem] lg:flex hidden">
                            <motion.div
                              initial={{ y: '-4rem' }}
                              whileInView={{ y: 0 }}
                              viewport={{ once: true, margin: '-100px' }}
                              transition={{ duration: 0.7, ease: easeOut, delay: 0.25 }}
                              className="relative"
                            >
                              <svg
                                className="w-[3rem] h-auto"
                                viewBox="0 0 24 35"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect x="11" width="2" height="50" fill="#011410"/>
                              </svg>
                            </motion.div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col items-center justify-center w-full">  
                    {item.childrens.length > 1 && (
                      <motion.div
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.75, delay: 0.5 }}
                        className="bg-fakeblack h-1 hidden lg:block w-full max-w-[89.5rem]"
                      />
                    )}

                    <div className="overflow-clip w-[3rem] h-[8.75rem] lg:h-auto flex items-center relative">
                      <motion.div
                        initial={{ y: '-5rem' }}
                        whileInView={{ y: 0 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.7, ease: easeOut, delay: 0.75 }}
                        className="relative"
                      >
                        <svg
                          className="w-[3rem] h-auto hidden lg:flex"
                          viewBox="0 0 24 40"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="11" width="2" height="20" fill="#011410"/>
                          <path d="M18.4425 29.4425L13 34.5V20H11V34.5L5.5575 29.4425L4 31L12 38.5L20 31L18.4425 29.4425Z" fill="#011410"/>
                        </svg>

                        <svg
                          className="w-[3rem] h-auto lg:hidden"
                          viewBox="0 0 24 69"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="11" width="2" height="50" fill="#011410"/>
                          <path d="M18.4425 59.4425L13 64.5V50H11V64.5L5.5575 59.4425L4 61L12 68.5L20 61L18.4425 59.4425Z" fill="#011410"/>
                        </svg>
                      </motion.div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Items Three */}
        <div className="flex flex-col lg:gap-[2rem] items-center justify-center w-full relative">
          {itemsThree?.length > 0 && itemsThree?.map((item, index) => (
            <div key={index} className="flex flex-col items-center justify-center lg:gap-[2.31rem] w-full relative">
              <motion.div
                className="flex items-center justify-center p-[1.25rem] mb-[1rem] lg:mb-0"
                initial={{ opacity: 0.08 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.25, ease: easeOut }}
              >
                <p className="text-[2rem] font-[400] text-fakeblack capitalize leading-[140%] font-inter-tight mb-0">{item.text}</p>
                <Image src={shapeRetangle} className="absolute w-[27.89rem] h-[5.5rem] z-0" alt="Shape Retangle" width={226} height={46} />
              </motion.div>

              {item.hasTools && item.tools && item.tools?.length > 0 && (
                <div className="flex flex-wrap lg:flex-row items-center justify-center gap-x-[5rem] gap-y-[1rem] w-full px-[4rem] lg:px-0 my-[1rem] lg:my-0">
                  {item.tools.map((tool, toolIndex) => {
                    const totalTools = item.tools!.length;
                    const isOdd = totalTools % 2 !== 0;
                    const middleIndex = Math.floor(totalTools / 2);
                    const isMiddle = isOdd && toolIndex === middleIndex;
                    const fromLeft = toolIndex < middleIndex;

                    return (
                      <motion.div
                        key={toolIndex}
                        initial={{ opacity: 0, x: isMiddle ? 0 : (fromLeft ? -50 : 50) }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, ease: easeOut, delay: 0.25 }}
                        className="w-auto h-[4rem] lg:h-[5rem]"
                      >
                        <Image
                          src={tool.src}
                          alt="Partner Logo"
                          className="w-auto h-[4rem] lg:h-[5rem] object-contain"
                          width={100}
                          height={80}
                        />
                      </motion.div>
                    );
                  })}
                </div>
              )}
              {!item.hasChildrens ? (
                <div className="overflow-clip w-[3rem] h-[8.75rem] flex lg:mt-[.6rem]">
                  <motion.div
                    initial={{ y: '-5rem', opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.7, ease: easeOut }}
                  >
                    <svg
                      className="w-[3rem] h-auto"
                      viewBox="0 0 24 69"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="11" width="2" height="50" fill="#011410"/>
                      <path d="M18.4425 59.4425L13 64.5V50H11V64.5L5.5575 59.4425L4 61L12 68.5L20 61L18.4425 59.4425Z" fill="#011410"/>
                    </svg>
                  </motion.div>
                </div>
              ) : (
                <div className="overflow-clip w-[3rem] h-[6rem] flex">
                  <motion.div
                    initial={{ y: '-5rem', opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.3, ease: easeOut }}
                  >
                    <svg
                      className="w-[3rem] h-auto"
                      viewBox="0 0 24 50"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="11" width="2" height="50" fill="#011410"/>
                    </svg>
                  </motion.div>
                </div>
              )}
              {item.hasChildrens && item.childrens && item.childrens?.length > 0 && (
                <div className="flex flex-col items-center justify-center w-full lg:-translate-y-[2.31rem] relative mb-[2rem] lg:mb-0">
                  {item.childrens.length > 1 && (
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true, margin: '-100px' }}
                      transition={{ duration: 0.75, delay: 0.25 }}
                      className="bg-fakeblack h-1 hidden lg:block w-full max-w-[89.25rem]"
                    />
                  )}

                  <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-between gap-[2.66rem] lg:gap-[7rem] w-full max-w-[136rem] border-[.35rem] lg:border-none border-fakeblack py-[5.94rem] lg:py-0">
                    {item.childrens.map((child, childIndex) => (
                      <div key={childIndex} className="flex flex-col items-center justify-center gap-[2.75rem] lg:w-[47rem]">
                        <div className="overflow-clip w-[3rem] lg:flex items-center hidden">
                          <motion.div
                            initial={{ y: '-5rem', opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            viewport={{ once: true, margin: '-100px' }}
                            transition={{ duration: 0.5, ease: easeOut, delay: 1 }}
                          >
                            <svg
                              className="w-[3rem] h-auto"
                              viewBox="0 0 24 43"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect x="11" width="2" height="24" fill="#011410"/>
                              <path d="M18.4425 33.4425L13 38.5V24H11V38.5L5.5575 33.4425L4 35L12 42.5L20 35L18.4425 33.4425Z" fill="#011410"/>
                            </svg>
                          </motion.div>
                        </div>
                        <div className="flex flex-col items-center justify-center gap-[2rem] lg:gap-[1rem]">
                          <motion.div
                            className="flex items-center justify-center p-[1.25rem] w-full lg:w-[27.89706rem]"
                            initial={{ opacity: 0.08 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true, margin: '-100px' }}
                            transition={{ duration: 0.25, ease: easeOut, delay: 0.25 }}
                          >
                            <p key={childIndex} className="text-[2rem] font-[400] text-fakeblack leading-[140%] font-inter-tight mb-0">{child.text}</p>
                            <Image src={shapeRetangle} className="absolute w-[27.89rem] h-[5.5rem] z-0" alt="Shape Retangle" width={226} height={46} />
                          </motion.div>
                          {child.hasTools && child.tools && child.tools?.length > 0 && (
                            <div className="flex flex-row items-center justify-center gap-x-[5rem] gap-y-[3rem] w-full">
                              {child.tools.map((tool, toolIndex) => {
                                const totalTools = child.tools!.length;
                                const isOdd = totalTools % 2 !== 0;
                                const middleIndex = Math.floor(totalTools / 2);
                                const isMiddle = isOdd && toolIndex === middleIndex;
                                const fromLeft = toolIndex < middleIndex;

                                return (
                                  <motion.div
                                    key={toolIndex}
                                    initial={{ opacity: 0, x: isMiddle ? 0 : (fromLeft ? -50 : 50) }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, ease: easeOut }}
                                    className="w-auto h-[4rem] lg:h-[5rem]"
                                  >
                                    <Image
                                      src={tool.src}
                                      alt="Partner Logo"
                                      className="w-auto h-[4rem] lg:h-[5rem] object-contain"
                                      width={100}
                                      height={80}
                                    />
                                  </motion.div>
                                );
                              })}
                            </div>
                          )}
                          <div className="overflow-clip w-[3rem] h-[4.5rem] lg:flex hidden">
                            <motion.div
                              initial={{ y: '-4.5rem', opacity: 0 }}
                              whileInView={{ y: 0, opacity: 1 }}
                              viewport={{ once: true, margin: '-100px' }}
                              transition={{ duration: 0.7, ease: easeOut, delay: 1 }}
                            >
                              <svg
                                className="w-[3rem] h-auto"
                                viewBox="0 0 24 35"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect x="11" width="2" height="50" fill="#011410"/>
                              </svg>
                            </motion.div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col items-center justify-center w-full">  
                    {item.childrens.length > 1 && (
                      <motion.div
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.75, delay: 1 }}
                        className="bg-fakeblack h-1 hidden lg:block w-full max-w-[89.25rem]"
                      />
                    )}

                    <div className="overflow-clip w-[3rem] h-[5rem] flex">
                      <motion.div
                        initial={{ y: '-5rem', opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.7, ease: easeOut, delay: 1.5 }}
                      >
                        <svg
                          className="w-[3rem] h-auto"
                          viewBox="0 0 24 39"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="11" width="2" height="20" fill="#011410"/>
                          <path d="M18.4425 29.4425L13 34.5V20H11V34.5L5.5575 29.4425L4 31L12 38.5L20 31L18.4425 29.4425Z" fill="#011410"/>
                        </svg>
                      </motion.div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Items Four */}
        <div className="flex flex-col gap-[2rem] items-center justify-center w-full relative">
          {itemsFour?.length > 0 && itemsFour?.map((item, index) => (
            <div key={index} className="flex flex-col items-center justify-center gap-[2rem] lg:gap-[1rem] w-full relative">
              <motion.div
                className="flex items-center justify-center p-[1.25rem]"
                initial={{ opacity: 0.08 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.25, ease: easeOut }}
              >
                <p className="text-[2rem] font-[400] text-fakeblack capitalize leading-[140%] font-inter-tight mb-0">{item.text}</p>
                <Image src={shapeRetangle} className="absolute w-[27.89rem] h-[5.5rem] z-0" alt="Shape Retangle" width={226} height={46} />
              </motion.div>

              {item.hasTools && item.tools && item.tools?.length > 0 && (
                <div className="flex flex-wrap lg:flex-row items-center justify-center gap-x-[5rem] gap-y-[2.5rem] w-full px-[3rem] lg:px-0">
                  {item.tools.map((tool, toolIndex) => {
                    const totalTools = item.tools!.length;
                    const isOdd = totalTools % 2 !== 0;
                    const middleIndex = Math.floor(totalTools / 2);
                    const isMiddle = isOdd && toolIndex === middleIndex;
                    const fromLeft = toolIndex < middleIndex;

                    return (
                      <motion.div
                        key={toolIndex}
                        initial={{ opacity: 0, x: isMiddle ? 0 : (fromLeft ? -50 : 50) }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, ease: easeOut, delay: 0.25 }}
                        className="w-auto h-[4rem] lg:h-[5rem]"
                      >
                        <Image
                          src={tool.src}
                          alt="Partner Logo"
                          className="w-auto h-[4rem] lg:h-[5rem] object-contain"
                          width={100}
                          height={80}
                        />
                      </motion.div>
                    );
                  })}
                </div>
              )}
              <div className="overflow-clip w-[3rem] h-[8.75rem] flex">
                <motion.div
                  initial={{ y: '-5rem', opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.7, ease: easeOut }}
                >
                  <svg
                    className="w-[3rem] h-auto"
                    viewBox="0 0 24 69"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="11" width="2" height="50" fill="#011410"/>
                    <path d="M18.4425 59.4425L13 64.5V50H11V64.5L5.5575 59.4425L4 61L12 68.5L20 61L18.4425 59.4425Z" fill="#011410"/>
                  </svg>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.25, ease: easeOut, delay: 0.5 }}
                className="w-full lg:w-[min(100%,29.625rem)]"
              >
                <CTAButton data={ctaButton} className="!w-full" />
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      {/* Background SVG */}
      <div className="absolute inset-x-0 top-[16rem] lg:top-[22rem] left-0 w-full h-auto flex flex-col items-center justify-center gap-[202rem] lg:gap-[79.62rem] z-0">
        <div className="flex flex-col gap-[7.76rem] lg:gap-[8rem] items-center justify-center w-full h-auto">
          <div className="flex items-center justify-between w-full">
            {/* Desktop */}
            <svg className="w-[60.5rem] h-auto hidden lg:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 485 297" fill="none">
              <path opacity="0.1" d="M2.6226e-06 1H161.367L484 296" stroke="#072922" stroke-width="2"/>
            </svg>
            <svg className="w-[60.5rem] h-auto hidden lg:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 485 297" fill="none">
              <path opacity="0.1" d="M484.675 1H323.308L0.674805 296" stroke="#072922" stroke-width="2"/>
            </svg>

            {/* Mobile */}
            <svg className="w-[12.125rem] h-auto lg:hidden" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 98 105" fill="none">
              <path opacity="0.1" d="M-5.84126e-06 1H32.34L97 103.89" stroke="#072922" stroke-width="2"/>
            </svg>
            <svg className="w-[12.125rem] h-auto lg:hidden" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 97 105" fill="none">
              <path opacity="0.1" d="M97.8467 1H65.5066L0.84668 103.89" stroke="#072922" stroke-width="2"/>
            </svg>
          </div>
          <div className="flex items-center justify-between w-full">
            {/* Desktop */}
            <svg className="w-[60.5rem] h-auto hidden lg:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 484 297" fill="none">
              <path opacity="0.1" d="M484 295.738L322.633 295.738L5.15794e-05 0.738039" stroke="#072922" stroke-width="2"/>
            </svg>
            <svg className="w-[60.5rem] h-auto hidden lg:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 484 297" fill="none">
              <path opacity="0.1" d="M2.6226e-06 295.738L161.367 295.738L484 0.738039" stroke="#072922" stroke-width="2"/>
            </svg>

            {/* Mobile */}
            <svg className="w-[12.125rem] h-auto lg:hidden" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 97 105" fill="none">
              <path opacity="0.1" d="M97 103.422L64.66 103.422L2.23519e-05 0.532054" stroke="#072922" stroke-width="2"/>
            </svg>
            <svg className="w-[12.125rem] h-auto lg:hidden" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 105" fill="none">
              <path opacity="0.1" d="M-5.84126e-06 103.422L32.34 103.422L97 0.532054" stroke="#072922" stroke-width="2"/>
            </svg>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-[21.1rem] lg:gap-[48.5rem] w-full h-auto left-0">
          <div className="flex items-center justify-between w-full">
            {/* Desktop */}
            <svg className="w-[60.5rem] h-auto hidden lg:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 484 297" fill="none">
              <path opacity="0.1" d="M484 295.738L322.633 295.738L5.15794e-05 0.738039" stroke="#072922" stroke-width="2"/>
            </svg>
            <svg className="w-[60.5rem] h-auto hidden lg:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 484 297" fill="none">
              <path opacity="0.1" d="M2.6226e-06 295.738L161.367 295.738L484 0.738039" stroke="#072922" stroke-width="2"/>
            </svg>

            {/* Mobile */}
            <svg className="w-[12.125rem] h-auto lg:hidden" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 97 105" fill="none">
              <path opacity="0.1" d="M97 103.422L64.66 103.422L2.23519e-05 0.532054" stroke="#072922" stroke-width="2"/>
            </svg>
            <svg className="w-[12.125rem] h-auto lg:hidden" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 105" fill="none">
              <path opacity="0.1" d="M-5.84126e-06 103.422L32.34 103.422L97 0.532054" stroke="#072922" stroke-width="2"/>
            </svg>
          </div>
          <div className="flex items-center justify-between w-full">
            {/* Desktop */}
            <svg className="w-[60.5rem] h-auto hidden lg:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 485 297" fill="none">
              <path opacity="0.1" d="M2.6226e-06 1H161.367L484 296" stroke="#072922" stroke-width="2"/>
            </svg>
            <svg className="w-[60.5rem] h-auto hidden lg:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 485 297" fill="none">
              <path opacity="0.1" d="M484.675 1H323.308L0.674805 296" stroke="#072922" stroke-width="2"/>
            </svg>

            {/* Mobile */}
            <svg className="w-[12.125rem] h-auto lg:hidden" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 98 105" fill="none">
              <path opacity="0.1" d="M-5.84126e-06 1H32.34L97 103.89" stroke="#072922" stroke-width="2"/>
            </svg>
            <svg className="w-[12.125rem] h-auto lg:hidden" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 97 105" fill="none">
              <path opacity="0.1" d="M97.8467 1H65.5066L0.84668 103.89" stroke="#072922" stroke-width="2"/>
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}
